import { Button, Center, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { usePermissions, type PermissionKey } from '../lib/permissions';

/**
 * Wrap a route's body in this guard so direct URL navigation to a
 * permission-gated page lands on a graceful empty state instead of
 * the page mounting, firing tRPC queries, and then surfacing a
 * "Permission denied" toast. The `<Children/>` only mount when the
 * permission check passes, so no protected query ever runs for a
 * member who lacks access.
 *
 * Use `permission="..."` for a fine-grained PermissionKey check, or
 * `adminOnly` for routes / sub-features that no team-member
 * permission unlocks (Settings/General, Subscription owner area).
 *
 * Backend `requirePermission(...)` stays as defence-in-depth — this
 * component just removes the user-visible 403 surface.
 */
export function PermissionGate({
  permission,
  adminOnly,
  children,
}: {
  permission?: PermissionKey;
  adminOnly?: boolean;
  children: React.ReactNode;
}) {
  const { can, isVendorAdmin, isSuperAdmin, isLoading } = usePermissions();
  // Don't flicker the empty state on first paint — wait for the
  // session payload to load before deciding.
  if (isLoading) return null;
  const allowed = adminOnly ? isVendorAdmin || isSuperAdmin : permission ? can(permission) : true;
  if (allowed) return <>{children}</>;
  return (
    <Center py="xl">
      <Stack align="center" gap="xs" maw={420} ta="center">
        <ThemeIcon size={56} radius="xl" variant="light" color="gray">
          <IconLock size={28} />
        </ThemeIcon>
        <Title order={4}>Not available for your account</Title>
        <Text c="dimmed" size="sm">
          Ask your workspace admin to grant access if you need this section.
        </Text>
        <Button component={Link} to="/vendor" variant="light" mt="sm">
          Back to dashboard
        </Button>
      </Stack>
    </Center>
  );
}
